import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import employeeService from "../../services/employeeService";
import employeeAttendanceService from "../../services/employeeAttendanceService";
import CollegePrintHeader from "../../components/common/CollegePrintHeader";

const REPORT_TYPES = [
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "yearly", label: "Yearly" },
  { value: "custom", label: "Custom Range" },
];

const TREND_SERIES = [
  { key: "present", source: "Present", label: "Present" },
  { key: "late", source: "Late", label: "Late" },
  { key: "halfDay", source: "Half Day", label: "Half Day" },
  { key: "onLeave", source: "On Leave", label: "On Leave" },
  { key: "medicalLeave", source: "Medical Leave", label: "Medical Leave" },
  { key: "absent", source: "Absent", label: "Absent" },
];

const getToday = () => new Date().toISOString().slice(0, 10);
const getMonthStart = () => {
  const now = new Date();
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1))
    .toISOString()
    .slice(0, 10);
};

const formatDisplayDate = (value = "") => {
  const [year, month, day] = value.split("-");
  if (!year || !month || !day) return value;
  return `${day}-${month}-${year}`;
};

export default function EmployeeAttendanceReport() {
  const [reportType, setReportType] = useState("monthly");
  const [department, setDepartment] = useState("");
  const [designation, setDesignation] = useState("");
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState(getMonthStart);
  const [toDate, setToDate] = useState(getToday);
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState("");
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [designationOptions, setDesignationOptions] = useState([]);

  useEffect(() => {
    employeeService
      .getAll({ limit: 500 })
      .then((res) => {
        const rows = res.data || [];
        const departments = [...new Set(rows.map((item) => item.department).filter(Boolean))];
        const designations = [...new Set(rows.map((item) => item.designation).filter(Boolean))];

        setDepartmentOptions(departments);
        setDesignationOptions(designations);
      })
      .catch((err) => toast.error(err.message));
  }, []);

  useEffect(() => {
    setLoading(true);
    employeeAttendanceService
      .getReport({
        reportType,
        department,
        designation,
        status,
        search,
        employeeId: selectedEmployeeId,
        ...(reportType === "custom" ? { fromDate, toDate } : {}),
      })
      .then((res) => setReport(res.data))
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  }, [reportType, department, designation, status, search, selectedEmployeeId, fromDate, toDate]);

  const selectedEmployee = useMemo(
    () =>
      (report?.employeeSummaries || []).find(
        (entry) => entry.employee._id === selectedEmployeeId,
      ) || null,
    [report, selectedEmployeeId],
  );

  const trendRows = useMemo(
    () =>
      (report?.trend || []).map((entry) =>
        TREND_SERIES.reduce(
          (acc, series) => ({
            ...acc,
            [series.key]: Number(entry?.[series.source] || 0),
          }),
          { date: entry.date },
        ),
      ),
    [report],
  );

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="attendance-print">
      <style>{`
        @media print {
          html, body {
            height: auto !important;
            overflow: visible !important;
            margin: 0 !important;
            padding: 0 !important;
            background: #fff !important;
          }

          body * {
            visibility: hidden !important;
          }

          .attendance-print,
          .attendance-print * {
            visibility: visible !important;
          }

          .attendance-print {
            position: static !important;
            width: 100% !important;
            padding: 0 !important;
            margin: 0 !important;
          }

          .app-layout,
          .main-content {
            display: block !important;
            margin: 0 !important;
            padding: 0 !important;
            width: 100% !important;
            min-height: auto !important;
          }

          .sidebar {
            display: none !important;
          }

          .no-print {
            display: none !important;
          }

          .print-block {
            display: block !important;
          }

          .print-table th,
          .print-table td {
            font-size: 11px !important;
            padding: 8px 10px !important;
          }

          .card {
            box-shadow: none !important;
            break-inside: avoid;
          }
        }
      `}</style>

      <div className="page-header no-print" style={{ flexWrap: "wrap", gap: "0.75rem" }}>
        <div>
          <h1 className="page-title">Employee Attendance Report</h1>
          <p className="page-subtitle">
            Review weekly, monthly, yearly, or custom employee attendance summaries.
          </p>
        </div>
        <button type="button" className="btn btn-primary" onClick={handlePrint}>
          Print Attendance Report
        </button>
      </div>

      <div className="card no-print" style={{ marginBottom: "1rem" }}>
        <div className="card-body">
          <div className="form-grid" style={{ alignItems: "end" }}>
            <div className="form-group">
              <label className="form-label">Report Type</label>
              <select className="form-control" value={reportType} onChange={(e) => setReportType(e.target.value)}>
                {REPORT_TYPES.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Department</label>
              <select className="form-control" value={department} onChange={(e) => setDepartment(e.target.value)}>
                <option value="">All Departments</option>
                {departmentOptions.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Designation</label>
              <select className="form-control" value={designation} onChange={(e) => setDesignation(e.target.value)}>
                <option value="">All Designations</option>
                {designationOptions.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Employee Status</label>
              <select className="form-control" value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="">All Statuses</option>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
                <option value="On Leave">On Leave</option>
                <option value="Resigned">Resigned</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Employee Search</label>
              <input
                className="form-control"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Name or employee code..."
              />
            </div>

            <div className="form-group">
              <label className="form-label">Employee Focus</label>
              <select
                className="form-control"
                value={selectedEmployeeId}
                onChange={(e) => setSelectedEmployeeId(e.target.value)}
              >
                <option value="">All Employees</option>
                {(report?.employeeSummaries || []).map((entry) => (
                  <option key={entry.employee._id} value={entry.employee._id}>
                    {entry.employee.fullName} ({entry.employee.employeeCode})
                  </option>
                ))}
              </select>
            </div>

            {reportType === "custom" && (
              <>
                <div className="form-group">
                  <label className="form-label">From Date</label>
                  <input className="form-control" type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">To Date</label>
                  <input className="form-control" type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="only-print" style={{ marginBottom: "1.25rem" }}>
        <CollegePrintHeader
          layout="banner"
          logoSize={58}
          title="EMPLOYEE ATTENDANCE REPORT"
          subtitle={`${REPORT_TYPES.find((item) => item.value === reportType)?.label || reportType} Report`}
          rightContent={
            <div style={{ fontSize: "11px", color: "#475569" }}>
              <div>Department: <strong>{department || "All Departments"}</strong></div>
              <div>Period: <strong>{report?.range ? `${formatDisplayDate(report.range.fromDate)} to ${formatDisplayDate(report.range.toDate)}` : "-"}</strong></div>
            </div>
          }
        />
      </div>

      {loading ? (
        <div className="card">
          <div className="loading-center">
            <div className="spinner" />
            Loading attendance report...
          </div>
        </div>
      ) : (
        <>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
              gap: "0.75rem",
              marginBottom: "1rem",
            }}
          >
            <div className="card">
              <div className="card-body" style={{ padding: "1rem" }}>
                <div style={{ fontSize: 12, color: "var(--text-muted)" }}>Working Days</div>
                <div style={{ fontSize: 24, fontWeight: 700 }}>
                  {report?.summary?.workingDays || 0}
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-body" style={{ padding: "1rem" }}>
                <div style={{ fontSize: 12, color: "var(--text-muted)" }}>Employees</div>
                <div style={{ fontSize: 24, fontWeight: 700 }}>
                  {report?.summary?.totalEmployees || 0}
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-body" style={{ padding: "1rem" }}>
                <div style={{ fontSize: 12, color: "var(--text-muted)" }}>Marked Entries</div>
                <div style={{ fontSize: 24, fontWeight: 700 }}>
                  {report?.summary?.totalMarked || 0}
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-body" style={{ padding: "1rem" }}>
                <div style={{ fontSize: 12, color: "var(--text-muted)" }}>Present %</div>
                <div style={{ fontSize: 24, fontWeight: 700, color: "#15803d" }}>
                  {report?.summary?.presentPercentage || 0}%
                </div>
              </div>
            </div>
          </div>

          <div className="card" style={{ marginBottom: "1rem" }}>
            <div className="card-header">
              {selectedEmployee ? "Individual Attendance Report" : "Attendance Trends"}
              {report?.range && (
                <span
                  style={{
                    marginLeft: 8,
                    fontWeight: 400,
                    color: "var(--text-muted)",
                    fontSize: 12,
                  }}
                >
                  {formatDisplayDate(report.range.fromDate)} to {formatDisplayDate(report.range.toDate)}
                </span>
              )}
            </div>
            <div className="card-body">
              {selectedEmployee ? (
                !selectedEmployee.entries?.length ? (
                  <div style={{ padding: "2rem 0", textAlign: "center", color: "var(--text-muted)" }}>
                    No attendance entries found for the selected employee.
                  </div>
                ) : (
                  <div className="table-wrapper">
                    <table className="print-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Status</th>
                          <th>Remarks</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedEmployee.entries.map((entry, index) => (
                          <tr key={`${entry.attendanceDate}-${entry.status}-${index}`}>
                            <td>{formatDisplayDate(entry.attendanceDate)}</td>
                            <td>{entry.status}</td>
                            <td>{entry.remarks || "-"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )
              ) : !trendRows.length ? (
                <div style={{ padding: "2rem 0", textAlign: "center", color: "var(--text-muted)" }}>
                  No attendance records found for the selected range.
                </div>
              ) : (
                <div className="table-wrapper">
                  <table className="print-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        {TREND_SERIES.map((series) => (
                          <th key={series.key}>{series.label}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {trendRows.map((entry) => (
                        <tr key={entry.date}>
                          <td>{formatDisplayDate(entry.date)}</td>
                          {TREND_SERIES.map((series) => (
                            <td key={series.key}>{entry[series.key]}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          <div className="card">
            <div className="card-header">Employee Attendance Summary</div>
            {!report?.employeeSummaries?.length ? (
              <div className="empty-state">
                <p>No employee-level attendance data found.</p>
              </div>
            ) : (
              <div className="table-wrapper">
                <table className="print-table">
                  <thead>
                    <tr>
                      <th>Employee</th>
                      <th>Code</th>
                      <th>Total Days</th>
                      <th>Present</th>
                      <th>Absent</th>
                      <th>Leave</th>
                      <th>Late</th>
                      <th>Attendance %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.employeeSummaries.map((entry) => (
                      <tr
                        key={entry.employee._id}
                        onClick={() => setSelectedEmployeeId(entry.employee._id)}
                        style={{
                          cursor: "pointer",
                          background:
                            entry.employee._id === selectedEmployeeId ? "#e8f1f8" : undefined,
                        }}
                      >
                        <td>
                          <div style={{ fontWeight: 600 }}>{entry.employee.fullName}</div>
                          <div style={{ fontSize: 11.5, color: "var(--text-muted)" }}>
                            {entry.employee.department} - {entry.employee.designation}
                          </div>
                        </td>
                        <td>{entry.employee.employeeCode}</td>
                        <td>{entry.totalDays}</td>
                        <td>{entry.presentDays}</td>
                        <td>{entry.absentDays}</td>
                        <td>{entry.leaveDays}</td>
                        <td>{entry.lateDays}</td>
                        <td>
                          <span
                            className="badge"
                            style={{
                              background:
                                entry.attendancePercentage >= 75 ? "#dcfce7" : "#fee2e2",
                              color:
                                entry.attendancePercentage >= 75 ? "#166534" : "#b91c1c",
                            }}
                          >
                            {entry.attendancePercentage}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
